import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ColorIconChooser from "@/components/ui/color-icon-chooser";
import { ProjectRenameDialog } from "@/features/sidebar/projects/dialogs/ProjectRenameDialog";
import { isPersonalProject } from "@/features/sidebar/projects/utils";
import { useNotificationRealtime } from "@/hooks/useNotificationRealtime";
import { useTheme } from "@/contexts/ThemeProvider";
import { getWorkspaceIconComponent } from "@/lib/constants/workspace-customization";
import { getWorkspaceImageUrlSync } from "@/lib/db/utils/supabase/storage/workspaceImages";
import { supabase } from "@/lib/supabase";
import useAuthStore from "@/store/authStore";
import { Info, X } from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

export function WorkspaceHeader({
  workspace,
  vectorStoreId,
  onUpdateWorkspace,
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [instructionsValue, setInstructionsValue] = useState("");
  const [includeInstructions, setIncludeInstructions] = useState(true);
  const [isSavingInstructions, setIsSavingInstructions] = useState(false);
  const [isInstructionsDialogOpen, setIsInstructionsDialogOpen] =
    useState(false);
  const [isColorIconChooserOpen, setIsColorIconChooserOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [isRenamingWorkspace, setIsRenamingWorkspace] = useState(false);
  const { workspaces, setWorkspaces } = useAuthStore();
  const { resolvedTheme } = useTheme();
  const location = useLocation();
  const previousWorkspaceIdRef = useRef(null);

  // Convert white (#f8f9fa) to black in light mode for visibility
  const getDisplayColor = (colorValue) => {
    if (colorValue === "#f8f9fa" && resolvedTheme === "light") {
      return "#000000";
    }
    return colorValue;
  };

  // Only subscribe to notifications if we have a workspace to watch
  // and we're not on a workflow page which causes subscription issues
  const isWorkflowPage = location.pathname.includes("/workflows/");
  const shouldSubscribe = !!workspace?.id && !isWorkflowPage;

  // Subscribe to realtime notifications
  const { isSubscribed } = useNotificationRealtime({
    enabled: shouldSubscribe,
    onInsert: (payload) => {
      // Check if this is a workspace_invitation for this workspace
      if (
        payload.new &&
        payload.new.type === "workspace_invitation" &&
        payload.new.workspace_id === workspace?.id
      ) {
        // Increment the pending invitation count or trigger a refresh
      }
    },
    onUpdate: (payload) => {
      // Handle accepted invitations
      if (
        payload.new &&
        payload.new.type === "workspace_invitation" &&
        payload.new.workspace_id === workspace?.id &&
        payload.new.status === "accepted" &&
        payload.old.status === "pending"
      ) {
        // Team members are now displayed in navbar, no local state to update
      }
    },
  });

  // Handle workspace image loading safely
  useEffect(() => {
    const currentWorkspaceId = workspace?.id;
    const previousWorkspaceId = previousWorkspaceIdRef.current;

    // Only reset image states if we're switching to a different workspace
    if (currentWorkspaceId !== previousWorkspaceId) {
      setImageLoaded(false);
      setImageError(false);
      setImageUrl(null);
      previousWorkspaceIdRef.current = currentWorkspaceId;
    }

    if (workspace?.is_team && (workspace.avatar_url || workspace.image_path)) {
      try {
        const url = getWorkspaceImageUrlSync(workspace.image_path);
        setImageUrl(url);
        setImageError(false);

        // Also attempt to load the image through the async method which will try member paths
        // This will update the image URL if a better source is found
        const loadWithFallbacks = async () => {
          try {
            const { data } = await supabase.auth.getUser();
            const userId = data?.user?.id;

            if (!userId) return;

            // If the user is not the workspace owner, try finding the image in their path first
            if (userId !== workspace.owner_id && workspace.image_path) {
              // Extract original path components
              const pathParts = workspace.image_path.split("/");
              if (pathParts.length >= 3) {
                const filename = pathParts[pathParts.length - 1];
                const workspaceId = pathParts[pathParts.length - 2];

                // Try the current user's path first
                const userPath = `${userId}/workspaces/${workspaceId}/${filename}`;

                const { data: imageData } = await supabase.storage
                  .from("avatars")
                  .createSignedUrl(userPath, 60 * 60); // 1 hour

                if (imageData?.signedUrl) {
                  setImageUrl(imageData.signedUrl);
                  return;
                }
              }
            }
          } catch (err) {
            // Error finding alternate image paths
          }
        };

        loadWithFallbacks();
      } catch (error) {
        setImageError(true);
      }
    } else if (currentWorkspaceId !== previousWorkspaceId) {
      // Only reset image states if this is a different workspace
      setImageUrl(null);
      setImageError(true);
    }
  }, [
    workspace?.id,
    workspace?.is_team,
    workspace?.avatar_url,
    workspace?.image_path,
    workspace?.owner_id,
  ]);


  // Initialize instructions value and toggle when workspace changes
  useEffect(() => {
    if (workspace?.instructions !== undefined) {
      setInstructionsValue(workspace.instructions || "");
    }
    // Initialize include_workspace_instructions (default to true if not set)
    setIncludeInstructions(workspace?.include_workspace_instructions ?? true);
  }, [workspace?.instructions, workspace?.include_workspace_instructions]);

  // Handle instructions change without auto-save
  const handleInstructionsChange = (e) => {
    const value = e.target.value;
    setInstructionsValue(value);
  };

  // Handle saving instructions from dialog
  const handleSaveInstructions = async () => {
    if (!onUpdateWorkspace) return;

    setIsSavingInstructions(true);
    try {
      await onUpdateWorkspace({
        instructions: instructionsValue,
        include_workspace_instructions: includeInstructions,
      });
      setIsInstructionsDialogOpen(false);
    } catch (error) {
      console.error("Error updating instructions:", error);
    } finally {
      setIsSavingInstructions(false);
    }
  };

  // Handle color/icon customization
  const handleColorSelect = async (color) => {
    if (!onUpdateWorkspace) return;
    try {
      await onUpdateWorkspace({
        metadata: {
          ...(workspace.metadata || {}),
          color: color.value,
        },
      });
    } catch (error) {
      console.error("Error updating workspace color:", error);
    }
  };

  const handleIconSelect = async (icon) => {
    if (!onUpdateWorkspace) return;
    try {
      await onUpdateWorkspace({
        metadata: {
          ...(workspace.metadata || {}),
          icon: icon.value,
        },
      });
    } catch (error) {
      console.error("Error updating workspace icon:", error);
    }
  };

  // Handler for opening rename dialog
  const handleOpenRenameDialog = () => {
    setNewWorkspaceName(workspace?.name || "");
    setIsRenameDialogOpen(true);
  };

  // Handler for renaming workspace
  const handleWorkspaceRename = async () => {
    if (!newWorkspaceName.trim() || !workspace?.id) return;
    setIsRenamingWorkspace(true);
    try {
      const { error } = await supabase
        .from("workspaces")
        .update({ name: newWorkspaceName.trim() })
        .eq("id", workspace.id);
      if (!error) {
        setWorkspaces(
          workspaces.map((w) =>
            w.id === workspace.id ? { ...w, name: newWorkspaceName.trim() } : w
          )
        );
        setIsRenameDialogOpen(false);
      }
    } catch (err) {
      console.error("Error renaming workspace:", err);
    } finally {
      setIsRenamingWorkspace(false);
    }
  };

  // Get initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return "??";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  if (!workspace) return null;

  return (
    <>
      <div className="relative mb-8 sm:mb-12 flex justify-center px-5 sm:px-0">
        <div className="w-full sm:w-3/4 max-w-4xl relative">
          <div className="absolute -inset-6 bg-gradient-to-r from-blue-900/10 via-indigo-900/10 to-blue-900/10 blur-xl opacity-70 -z-10 rounded-2xl"></div>

          {/* Simplified Header: Icon + Name + Team Avatars | Instructions Button */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Left side: Icon + Name + Team Avatars */}
            <div className="flex items-center gap-3">
              {/* Workspace Icon */}
              <ColorIconChooser
                isOpen={isColorIconChooserOpen}
                onOpenChange={setIsColorIconChooserOpen}
                currentColor={workspace.metadata?.color || "#f8f9fa"}
                currentIcon={workspace.metadata?.icon || "folder"}
                onColorSelect={handleColorSelect}
                onIconSelect={handleIconSelect}
              >
                <button
                  className="relative cursor-pointer hover:opacity-80 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 rounded-lg flex-shrink-0"
                  aria-label="Customize project icon and color"
                  onClick={() => setIsColorIconChooserOpen(true)}
                >
                  {imageUrl && !imageError ? (
                    <img
                      src={imageUrl}
                      alt={`${workspace.name} logo`}
                      className={`w-12 h-12 rounded-lg object-cover transition-opacity duration-200 ${
                        imageLoaded ? "opacity-100" : "opacity-0"
                      }`}
                      onLoad={() => setImageLoaded(true)}
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    (() => {
                      const IconComponent = getWorkspaceIconComponent(
                        workspace.metadata?.icon || "folder"
                      );
                      const rawColor = workspace.metadata?.color || "#f8f9fa";
                      const bgColor = getDisplayColor(rawColor);
                      // Use dark icon for light backgrounds (white/black converted from white)
                      const iconColor = bgColor === "#000000" ? "text-white" : (rawColor === "#f8f9fa" ? "text-black" : "text-white");
                      return (
                        <div
                          className="w-12 h-12 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: bgColor }}
                        >
                          <IconComponent className={`w-6 h-6 ${iconColor}`} />
                        </div>
                      );
                    })()
                  )}
                </button>
              </ColorIconChooser>

              {/* Workspace Name - clickable to rename (except for Personal Project) */}
              {!isPersonalProject(workspace) ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleOpenRenameDialog}
                        className="text-xl font-semibold text-gray-800 dark:text-white line-clamp-1 hover:text-gray-600 dark:hover:text-zinc-300 transition-colors cursor-pointer bg-transparent border-none p-0 text-left"
                      >
                        {workspace.name}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Click to rename</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <h1 className="text-xl font-semibold text-gray-800 dark:text-white line-clamp-1">
                  {workspace.name}
                </h1>
              )}
            </div>

            {/* Right side: Instructions Button */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                onClick={() => setIsInstructionsDialogOpen(true)}
                className="bg-transparent border-gray-200 dark:border-zinc-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-all duration-200 flex items-center gap-2 border-dashed"
              >
                <Info className="h-4 w-4" />
                <span>Instructions</span>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  optional
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions Dialog */}
      <Dialog
        open={isInstructionsDialogOpen}
        onOpenChange={setIsInstructionsDialogOpen}
      >
        <DialogContent
          className="max-w-[90vw] w-[600px] bg-white dark:bg-zinc-900 border-[#3a3a3a] text-white p-0 gap-0 rounded-2xl flex flex-col overflow-hidden"
          hideCloseButton={true}
          style={{ zIndex: 9999, maxHeight: "min(90vh, 500px)" }}
        >
          <DialogTitle className="sr-only">Project Instructions</DialogTitle>
          {/* <DialogDescription className="sr-only">
            Set instructions for how assistants should behave in this project
          </DialogDescription> */}

          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex-shrink-0 rounded-t-2xl">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Project Instructions
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsInstructionsDialogOpen(false)}
                className="h-8 w-8 bg-transparent hover:bg-transparent text-gray-900 dark:text-white hover:text-gray-900 dark:hover:text-white border-none"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-900 rounded-b-2xl">
            <div className="flex-1 overflow-auto">
              <div className="p-6">
                {/* Include instructions toggle */}
                <div className="flex items-center justify-between p-3 mb-4 bg-gray-50 dark:bg-zinc-800/50 rounded-lg border border-gray-200 dark:border-zinc-700">
                  <div className="flex flex-col">
                    <Label
                      htmlFor="include-instructions"
                      className="text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Include workspace instructions
                    </Label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      When enabled, these instructions are included in AI
                      prompts
                    </p>
                  </div>
                  <Switch
                    id="include-instructions"
                    checked={includeInstructions}
                    onCheckedChange={setIncludeInstructions}
                    disabled={isSavingInstructions}
                  />
                </div>

                <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                  Set instructions for how assistants should behave in this
                  project
                </p>
                <Textarea
                  value={instructionsValue}
                  onChange={handleInstructionsChange}
                  placeholder="Enter instructions for assistants in this project..."
                  className="min-h-[120px] max-h-[200px] text-sm border-gray-200 dark:border-zinc-700 bg-transparent text-gray-900 dark:text-zinc-100 resize-none"
                  disabled={isSavingInstructions}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex-shrink-0">
              <Button
                onClick={handleSaveInstructions}
                disabled={isSavingInstructions}
                className="bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-100 rounded-2xl px-6"
              >
                {isSavingInstructions ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border border-current border-t-transparent rounded-full" />
                    Saving...
                  </div>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename Project Dialog */}
      <ProjectRenameDialog
        isOpen={isRenameDialogOpen}
        onOpenChange={setIsRenameDialogOpen}
        workspaceToRename={workspace}
        newWorkspaceName={newWorkspaceName}
        setNewWorkspaceName={setNewWorkspaceName}
        isRenamingWorkspace={isRenamingWorkspace}
        onRename={handleWorkspaceRename}
        onCancel={() => setIsRenameDialogOpen(false)}
      />
    </>
  );
}

WorkspaceHeader.propTypes = {
  workspace: PropTypes.object,
  vectorStoreId: PropTypes.string,
  onUpdateWorkspace: PropTypes.func,
};
export default WorkspaceHeader;
